import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
  CircularProgress
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  AccountBalanceWallet as WalletIcon,
  Business as ProjectIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  ChevronRight
} from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getCustomerProjectsAPI, getProjectDetailsAPI, getProjectTeamMembersAPI } from '../../lib/api';
import EditProjectForm from '../projects/EditProjectForm';
import TeamMembers from './TeamMembers';
import Procurements from './Procurements';
import Documents from './Documents';
import BudgetCategories from './BudgetCategories';
import LoadingProgress from '../../components/common/LoadingProgress';

const ProjectDetails = ({ project, onBack, customerId }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showTeamMembers, setShowTeamMembers] = useState(false);
  const [showProcurements, setShowProcurements] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showBudgetCategories, setShowBudgetCategories] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleEditSave = (updatedProject) => {
  };

  useEffect(() => {
    const isMembers = /\/members(\/|$)/.test(location.pathname);
    const isProcurements = /\/procurements(\/|$)/.test(location.pathname);
    const isDocuments = /\/documents(\/|$)/.test(location.pathname);
    const isBudgetCategories = /\/budget-categories(\/|$)/.test(location.pathname);
    setShowTeamMembers(isMembers);
    setShowProcurements(isProcurements);
    setShowDocuments(isDocuments);
    setShowBudgetCategories(isBudgetCategories);
  }, [location.pathname]);

  const handleTeamMembersClick = () => {
    navigate(`/customers/${customerId}/project/${project.id}/members`);
  };

  const handleBackFromTeamMembers = () => {
    navigate(`/customers/${customerId}/project/${project.id}`);
  };

  const handleProcurementsClick = () => {
    navigate(`/customers/${customerId}/project/${project.id}/procurements`);
  };

  const handleBackFromProcurements = () => {
    navigate(`/customers/${customerId}/project/${project.id}`);
  };

  const handleDocumentsClick = () => {
    navigate(`/customers/${customerId}/project/${project.id}/documents`);
  };

  const handleBackFromDocuments = () => {
    navigate(`/customers/${customerId}/project/${project.id}`);
  };

  const handleBudgetCategoriesClick = () => {
    navigate(`/customers/${customerId}/project/${project.id}/budget-categories`);
  };

  const handleBackFromBudgetCategories = () => {
    navigate(`/customers/${customerId}/project/${project.id}`);
  };

  if (showBudgetCategories) {
    return (
      <BudgetCategories
        customerId={customerId}
        projectId={project.id}
        projectName={project.name}
        onBack={handleBackFromBudgetCategories}
      />
    );
  }

  if (showDocuments) {
    return (
      <Documents
        customerId={customerId}
        projectId={project.id}
        projectName={project.name}
        onBack={handleBackFromDocuments}
      />
    );
  }

  if (showProcurements) {
    return (
      <Procurements
        customerId={customerId}
        projectId={project.id}
        projectName={project.name}
        onBack={handleBackFromProcurements}
      />
    );
  }

  if (showTeamMembers) {
    return (
      <TeamMembers
        customerId={customerId}
        projectId={project.id}
        projectName={project.name}
        onBack={handleBackFromTeamMembers}
      />
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 2 }, 
      bgcolor: '#ffffff', 
      minHeight: 'auto',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 2, sm: 1 },
        mb: { xs: 2, sm: 2, md: 3 } 
      }}>
        <Box sx={{              
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1.5, sm: 2 },
          flex: 1,
          minWidth: 0,
          width: { xs: '100%', sm: 'auto' },
          overflow: 'hidden'
        }}>
          <IconButton 
            onClick={onBack} 
            sx={{ 
              color: colors.primary.main,
              bgcolor: colors.background.hover,
              '&:hover': {
                bgcolor: colors.primary.light + '20'
              },
              flexShrink: 0,
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
          <Typography 
            variant="h5" 
            fontWeight="bold"
            sx={{
              color: colors.text.primary,
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem', lg: '1.5rem' },
              wordBreak: 'break-word',
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              maxWidth: 'calc(100% - 48px)'
            }}
          >
            {project.name}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 1 }, 
          flexShrink: 0,
          width: { xs: '100%', sm: 'auto' }
        }}>
          {/* <Button 
            variant="contained" 
            onClick={handleEditClick}
            sx={{ 
              bgcolor: colors.primary.main,
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              flex: { xs: 1, sm: 'none' },
              minWidth: { xs: 'auto', sm: '100px' },
              '&:hover': {
                bgcolor: colors.primary.dark
              }
            }}
            startIcon={<EditIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
          >
            Edit
          </Button>
          <Button 
            variant="contained" 
            sx={{
              bgcolor: colors.primary.main,
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              flex: { xs: 1, sm: 'none' },
              minWidth: { xs: 'auto', sm: '100px' },
              '&:hover': {
                bgcolor: colors.primary.dark
              }
            }}
            startIcon={<ChatIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
          >
            Chat
          </Button> */}
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            onClick={handleBudgetCategoriesClick}
            sx={{
             height: '100%',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              bgcolor: '#ffffff',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)'
              }
            }}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Budget Used
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#4caf50',
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem' }
                  }}
                >
                  {formatAmount(project.used_amount || 0)}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  / {formatAmount(project.allocated_amount)}
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0, ml: 1 }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={60}
                  thickness={4}
                  sx={{
                    color: colors.alpha.black08,
                    position: 'absolute',
                  }}
                />
                <CircularProgress
                  variant="determinate"
                  value={project.used_percentage || 0}
                  size={60}
                  thickness={4}
                  sx={{
                    color: '#4caf50',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    }
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '0.813rem' }
                    }}
                  >
                    {`${Math.round(project.used_percentage || 0)}%`}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                right: { xs: 8, sm: 12 },
                color: colors.text.secondary,
                '&:hover': {
                  color: colors.primary.main
                }
              }}
            >
              <ChevronRight sx={{ fontSize: { xs: 16, sm: 20 } }} />
            </IconButton>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{
            height: '100%',
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            bgcolor: '#ffffff'
          }}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Time line
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                wordBreak: 'break-word',
                lineHeight: 1.4
              }}
            >
              {formatDate(project.start_date)} to {formatDate(project.end_date)}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={12} md={4}>
          <Card
            elevation={0}
            onClick={handleProcurementsClick}
            sx={{
              height: '100%',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: '1px solid #e0e0e0',
                  bgcolor: '#ffffff',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Procurement
            </Typography>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mb: 0.5
                }}
              >
                Pending: {project.procurement_status?.pending || 0} ({formatAmount(project.procurement_amounts?.pending || 0)})
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mb: 0.5
                }}
              >
                Approved: {project.procurement_status?.approved || 0} ({formatAmount(project.procurement_amounts?.approved || 0)})
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500, 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Rejected: {project.procurement_status?.rejected || 0} ({formatAmount(project.procurement_amounts?.rejected || 0)})
              </Typography>
            </Box>
            <IconButton
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                right: { xs: 8, sm: 12 },
                color: colors.text.secondary,
                '&:hover': {
                  color: colors.primary.main
                }
              }}
            >
              <ChevronRight sx={{ fontSize: { xs: 16, sm: 20 } }} />
            </IconButton>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            onClick={handleTeamMembersClick}
            sx={{
              height: '100%',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              bgcolor: '#ffffff',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Team members
            </Typography>
            <IconButton
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                right: { xs: 8, sm: 12 },
                color: colors.text.secondary,
                '&:hover': {
                  color: colors.primary.main
                }
              }}
            >
              <ChevronRight sx={{ fontSize: { xs: 16, sm: 20 } }} />
            </IconButton>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            onClick={handleDocumentsClick}
            sx={{
              height: '100%',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              bgcolor: '#ffffff',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' }             
              }}
            >
              Documents
            </Typography>
            <IconButton
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                right: { xs: 8, sm: 12 },
                color: colors.text.secondary,
                '&:hover': {
                  color: colors.primary.main
                }
              }}
            >
              <ChevronRight sx={{ fontSize: { xs: 16, sm: 20 } }} />
            </IconButton>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{
            height: '100%',
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            bgcolor: '#ffffff',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)'
            }
          }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Progress report
            </Typography>
            <IconButton
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                right: { xs: 8, sm: 12 },
                color: colors.text.secondary,
                '&:hover': {
                  color: colors.primary.main
                }
              }}
            >
              <ChevronRight sx={{ fontSize: { xs: 16, sm: 20 } }} />
            </IconButton>
          </Card>
        </Grid> */}
      </Grid>

      {/* <Box sx={{ mt: { xs: 2, sm: 2 } }}>
        <Typography 
          variant="h6" 
          mb={{ xs: 2, sm: 2 }}
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
          }}
        >
          Cost
        </Typography>
        <Box
          sx={{
            height: { xs: 200, sm: 250, md: 280 },
            border: '1px solid #e0e0e0',
            borderRadius: 3,
            bgcolor: '#ffffff',
            p: { xs: 1.5, sm: 2 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            position: 'absolute',
            left: { xs: 8, sm: 12, md: 15 },
            top: { xs: 8, sm: 12, md: 15 },
            bottom: { xs: 20, sm: 25, md: 30 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            zIndex: 1
          }}>
            {['$50L', '$40L', '$30L', '$20L', '$10L', '$0'].map((label) => (
              <Typography
                key={label}
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 500,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' }
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          <Box sx={{
            position: 'absolute',
            left: { xs: 30, sm: 40, md: 45 },
            top: { xs: 8, sm: 12, md: 15 },
            right: { xs: 8, sm: 12, md: 15 },
            bottom: { xs: 20, sm: 25, md: 30 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            {[0, 1, 2, 3, 4, 5].map((line) => (
              <Box
                key={line}
                sx={{
                  height: '1px',
                  bgcolor: '#f0f0f0',
                  width: '100%'
                }}
              />
            ))}
          </Box>

          <Box sx={{
            position: 'absolute',
            left: { xs: 30, sm: 40, md: 45 },
            right: { xs: 8, sm: 12, md: 15 },
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: { xs: 20, sm: 25, md: 30 }
          }}>
            {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'].map((month) => (
              <Typography
                key={month}
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 500,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  textAlign: 'center',
                  flex: 1
                }}
              >
                {month}
              </Typography>
            ))}
          </Box>

          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: colors.text.secondary,
            zIndex: 2
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.6,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              [Cost Chart Placeholder]
            </Typography>
          </Box>
        </Box>
      </Box> */}

      <EditProjectForm
        open={editModalOpen}
        onClose={handleEditClose}
        project={project}
        onSave={handleEditSave}
      />
    </Box>
  );
};

const ProjectCard = ({ project, isModal = false, onProjectClick }) => {

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getProjectStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Unknown';
    
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (now < start) return 'Upcoming';
    if (now > end) return 'Completed';
    return 'Active';
  };

  const status = getProjectStatus(project.start_date, project.end_date);

  return (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: colors.background.primary,
      border: `1px solid ${colors.alpha.black08}`,
      '&:hover': {
        cursor: 'pointer'
      },
      borderRadius: 3,
      overflow: 'visible',
      minHeight: 'fit-content',
      minWidth: { xs: '100%', sm: 'auto' }
    }}
    onClick={() => onProjectClick && onProjectClick(project)}
  >
    <CardContent 
      sx={{ 
        flexGrow: 1, 
        p: 0,
        '&:last-child': {
          paddingBottom: 0
        }
      }}
    >
      <Box sx={{
        background: `linear-gradient(135deg, ${colors.background.card} 0%, ${colors.background.hover} 100%)`,
        p: 2.5
      }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 1
        }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              lineHeight: 1.3,
              wordBreak: 'break-word',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              flex: 1,
              whiteSpace: 'normal',
              overflow: 'visible',
              minHeight: 'fit-content'
            }}
          >
            {project.name}
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              backgroundColor: status === 'Active' ? colors.success.light : 
                              status === 'Completed' ? colors.gray[100] :
                              status === 'Upcoming' ? colors.info.light : colors.gray[100],
              borderRadius: 1,
              flexShrink: 0
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: status === 'Active' ? colors.success.main : 
                       status === 'Completed' ? colors.text.tertiary :
                       status === 'Upcoming' ? colors.info.main : colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem'
              }}
            >
              {status}
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body2" sx={{
          color: colors.text.secondary,
          fontSize: '0.8rem'
        }}>
          ID: #{project.id}
        </Typography>
      </Box>

      <Box sx={{ 
        p: { xs: 1.5, sm: 2 },
        width: '100%',
        overflow: 'visible'
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr',
            sm: 'repeat(auto-fit, minmax(180px, 1fr))',
            md: '1fr 1fr'
          },
          gap: { xs: 1.5, sm: 2 },
          width: '100%'
        }}>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: { xs: 1, sm: 1.5 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: colors.alpha.white60,
            borderRadius: 1.5,
            border: `1px solid ${colors.alpha.black04}`,
            width: '100%',
            minWidth: 0,
            minHeight: 'fit-content',
            overflow: 'visible'
          }}>
            <LocationIcon sx={{ 
              color: colors.text.secondary, 
              fontSize: { xs: 16, sm: 18 },
              flexShrink: 0
            }} />
            <Box sx={{ 
              flex: 1, 
              minWidth: 0, 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              overflow: 'visible'
            }}>
              <Typography variant="caption" sx={{ 
                color: colors.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                display: 'block',
                mb: 0.25,
                fontSize: { xs: '0.65rem', sm: '0.7rem' }
              }}>
                Location
              </Typography>
              <Typography variant="body2" sx={{ 
                color: colors.text.primary,
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                wordBreak: 'break-word',
                lineHeight: 1.4,
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset',
                minHeight: 'fit-content'
              }}>
                {project.location || 'Not specified'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: { xs: 1, sm: 1.5 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: colors.alpha.white60,
            borderRadius: 1.5,
            border: `1px solid ${colors.alpha.black04}`,
            width: '100%',
            minWidth: 0,
            minHeight: 'fit-content',
            overflow: 'visible'
          }}>
            <WalletIcon sx={{ 
              color: colors.text.secondary, 
              fontSize: { xs: 16, sm: 18 },
              flexShrink: 0
            }} />
            <Box sx={{ 
              flex: 1, 
              minWidth: 0, 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              overflow: 'visible'
            }}>
              <Typography variant="caption" sx={{ 
                color: colors.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                display: 'block',
                mb: 0.25,
                fontSize: { xs: '0.65rem', sm: '0.7rem' }
              }}>
                Budget
              </Typography>
              <Typography variant="body2" sx={{ 
                color: colors.text.primary,
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset'
              }}>
                {formatAmount(project.allocated_amount)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: { xs: 1, sm: 1.5 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: 1.5,
            border: '1px solid rgba(0,0,0,0.04)',
            width: '100%',
            minWidth: 0,
            minHeight: 'fit-content',
            overflow: 'visible'
          }}>
            <CalendarIcon sx={{ 
              color: colors.text.secondary, 
              fontSize: { xs: 16, sm: 18 },
              flexShrink: 0
            }} />
            <Box sx={{ 
              flex: 1, 
              minWidth: 0, 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              overflow: 'visible'
            }}>
              <Typography variant="caption" sx={{ 
                color: colors.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                display: 'block',
                mb: 0.25,
                fontSize: { xs: '0.65rem', sm: '0.7rem' }
              }}>
                Start Date
              </Typography>
              <Typography variant="body2" sx={{ 
                color: colors.text.primary,
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset'
              }}>
                {formatDate(project.start_date)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: { xs: 1, sm: 1.5 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: 'rgba(255, 255, 255, 0.6)',         
            borderRadius: 1.5,
            border: '1px solid rgba(0,0,0,0.04)',
            width: '100%',
            minWidth: 0,
            minHeight: 'fit-content',
            overflow: 'visible'
          }}>
            <CalendarIcon sx={{ 
              color: colors.text.secondary, 
              fontSize: { xs: 16, sm: 18 },
              flexShrink: 0
            }} />
            <Box sx={{ 
              flex: 1, 
              minWidth: 0, 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              overflow: 'visible'
            }}>
              <Typography variant="caption" sx={{ 
                color: colors.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                display: 'block',
                mb: 0.25,
                fontSize: { xs: '0.65rem', sm: '0.7rem' }
              }}>
                End Date
              </Typography>
              <Typography variant="body2" sx={{ 
                color: colors.text.primary,
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset'
              }}>
                {formatDate(project.end_date)}
              </Typography>
            </Box>
          </Box>

        </Box>
      </Box>
    </CardContent>
  </Card>
);

};

const CustomerProjects = ({ customerId = 1, isModal = false }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const routeCustomerId = params.id;
  const routeProjectId = params.projectId;
  const effectiveCustomerId = customerId || routeCustomerId;

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getCustomerProjectsAPI(effectiveCustomerId);
      if (result.status && result.data) {
        setProjects(Array.isArray(result.data) ? result.data : []);
      } else {
        setError('Failed to load projects data');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch customer projects');
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (effectiveCustomerId) {
      fetchProjects();
    }
  }, [effectiveCustomerId]);

  useEffect(() => {
    const ensureProjectSelected = async () => {
      if (!routeProjectId) {
        setSelectedProject(null);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const result = await getProjectDetailsAPI(effectiveCustomerId, routeProjectId);
        if (result.status && result.data) {
          setSelectedProject(result.data);
        } else {
          const fallback = projects.find(p => String(p.id) === String(routeProjectId));
          if (fallback) setSelectedProject(fallback);
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
        const fallback = projects.find(p => String(p.id) === String(routeProjectId));
        if (fallback) setSelectedProject(fallback);
      } finally {
        setLoading(false);
      }
    };

    ensureProjectSelected();
  }, [routeProjectId, effectiveCustomerId]);

  const handleProjectClick = async (project) => {
    try {
      setLoading(true);
      setError('');
      const result = await getProjectDetailsAPI(effectiveCustomerId, project.id);
      if (result.status && result.data) {
        setSelectedProject(result.data);
      } else {
        setError('Failed to load project details');
        setSelectedProject(project);
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch project details');
      console.error('Error fetching project details:', error);
      setSelectedProject(project);
    } finally {
      setLoading(false);
    }

    navigate(`/customers/${effectiveCustomerId}/project/${project.id}`);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    if (routeProjectId) {
      navigate(`/customers/${effectiveCustomerId}/project`);
    }
  };

  if (selectedProject || routeProjectId) {
    return (
      <Box
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: isModal ? 'auto' : '400px'
        }}
      >
        <ProjectDetails
          project={selectedProject || { id: routeProjectId, name: '' }}
          onBack={handleBackToProjects}
          customerId={effectiveCustomerId}
        />
      </Box>
    );
  }

  return (
    <>
      <LoadingProgress loading={loading} />
      <Box
        sx={{
          p: 0,
          display: 'flex',
          minHeight: isModal ? 'auto' : '400px'
        }}
      >

        {isModal && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 2, sm: 0 },
          mb: 3,
          pb: 3,
        }}>
          <Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
                display: 'flex',
                alignItems: 'center',
                mb: 0.5
              }}
            >
              <ProjectIcon sx={{ mr: 1.5, fontSize: 24, color: colors.text.secondary }} />
              Customer Projects
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              View all projects associated with this customer
            </Typography>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {projects.length === 0 && !loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: isModal ? 6 : 8,
            textAlign: 'center',
            background: isModal ? `linear-gradient(135deg, ${colors.background.card} 0%, ${colors.slate[200]} 100%)` : 'transparent',
            borderRadius: isModal ? 2 : 0,
            border: 'none',
            width: '100%',
            minHeight: '300px'
          }}
        >
          <ProjectIcon sx={{      
            fontSize: isModal ? 48 : 64, 
            color: colors.text.disabled, 
            mb: 2
          }} />
          <Typography variant={isModal ? "body1" : "h6"} sx={{  
            color: colors.text.tertiary,
            mb: 1,
            fontWeight: 600      
          }}>
            No Projects Found
          </Typography>
          <Typography variant="body2" sx={{
            color: colors.text.secondary,
            maxWidth: '280px'
          }}>
            This customer doesn't have any projects assigned yet.
          </Typography>
        </Box>
      ) : (
        !loading && (
          <Grid container spacing={{ xs: 2, sm: isModal ? 3 : 4 }} sx={{ alignItems: 'stretch' }}>
            {projects.map((project) => (
              <Grid
                item
                xs={12}
                sm={isModal ? 12 : 6}
                md={isModal ? 12 : 6}
                lg={isModal ? 12 : 6}
                xl={isModal ? 12 : 4}
                key={project.id}
                sx={{
                  display: 'flex',
                  minWidth: 0,
                  width: '100%'
                }}
              >
                <ProjectCard
                  project={project}
                  isModal={isModal}
                  onProjectClick={handleProjectClick}
                />
              </Grid>
            ))}
          </Grid>
        )
      )}

      {/* {!loading && projects.length > 0 && (
        <Box sx={{
          mt: 3,
          pt: 2,
          borderTop: 'none',
          textAlign: 'center'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{
            fontWeight: 500
          }}>
            {projects.length === 1
              ? '1 project found'
              : `${projects.length} projects found`
            }
          </Typography>
        </Box>
      )} */}
      </Box>
    </>
  );
};

export default CustomerProjects;